import {
  Component,
  ElementRef,
  ViewChild,
  computed,
  input,
  model,
  signal,
} from '@angular/core';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

@Component({
  selector: 'app-email-tags-input',
  standalone: true,
  template: `
    <div
      class="cg-email-tags"
      [class.cg-email-tags--disabled]="disabled()"
      [class.cg-email-tags--focused]="focused()"
      (click)="focusInput()"
    >
      @for (email of emails(); track email; let i = $index) {
        <span
          class="cg-email-tags__chip"
          [class.cg-email-tags__chip--selected]="selectedIndex() === i"
        >
          <span class="cg-email-tags__chip-text">{{ email }}</span>
          <button
            type="button"
            class="cg-email-tags__chip-remove"
            [disabled]="disabled()"
            [attr.aria-label]="'Remove ' + email"
            (click)="removeAt(i); $event.stopPropagation()"
          >
            ×
          </button>
        </span>
      }

      <input
        #draftInput
        class="cg-email-tags__input"
        type="text"
        [disabled]="disabled()"
        [placeholder]="emails().length === 0 ? placeholder() : ''"
        [value]="draft()"
        (input)="onDraftInput($event)"
        (keydown)="onKeydown($event)"
        (blur)="onBlur()"
        (focus)="onFocus()"
        (paste)="onPaste($event)"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
      />
    </div>

    @if (error()) {
      <p class="cg-form-error" role="alert">{{ error() }}</p>
    } @else if (info()) {
      <p class="cg-form-help" role="status">{{ info() }}</p>
    }
  `,
})
export class EmailTagsInputComponent {
  readonly emails = model<string[]>([]);
  readonly placeholder = input('Add email address');
  readonly disabled = input(false);
  /** Emails that already exist elsewhere (e.g. To) and should not be added here. */
  readonly excludedEmails = input<string[]>([]);

  @ViewChild('draftInput') private draftInput?: ElementRef<HTMLInputElement>;

  readonly draft = signal('');
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly selectedIndex = signal<number | null>(null);
  readonly focused = signal(false);

  readonly normalizedExcluded = computed(() =>
    new Set(this.excludedEmails().map((e) => e.trim().toLowerCase()).filter(Boolean)),
  );

  focusInput(): void {
    if (this.disabled()) return;
    this.draftInput?.nativeElement.focus();
  }

  clearMessages(): void {
    this.error.set(null);
    this.info.set(null);
  }

  reset(): void {
    this.emails.set([]);
    this.draft.set('');
    this.selectedIndex.set(null);
    this.clearMessages();
  }

  /** Commit any remaining draft before parent submit. Returns false if draft is invalid. */
  commitPending(): boolean {
    const value = this.draft().trim();
    if (!value) {
      this.clearMessages();
      return true;
    }
    return this.tryCommit(value);
  }

  removeAt(index: number): void {
    if (this.disabled()) return;
    const next = this.emails().filter((_, i) => i !== index);
    this.emails.set(next);
    this.selectedIndex.set(null);
    this.clearMessages();
    this.focusInput();
  }

  onFocus(): void {
    this.focused.set(true);
  }

  onBlur(): void {
    this.focused.set(false);
    this.selectedIndex.set(null);
    const value = this.draft().trim();
    if (!value) {
      this.clearMessages();
      return;
    }
    this.tryCommit(value);
  }

  onDraftInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.selectedIndex.set(null);
    this.clearMessages();

    // Commit when user types a trailing separator (comma/space).
    if (/[,\s]$/.test(value)) {
      const trimmed = value.replace(/[,\s]+$/, '').trim();
      if (trimmed) {
        this.tryCommit(trimmed);
        return;
      }
      this.draft.set('');
      return;
    }

    this.draft.set(value);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    const key = event.key;
    const draft = this.draft();
    const emails = this.emails();

    if (key === 'Backspace' && draft.length === 0 && emails.length > 0) {
      event.preventDefault();
      const selected = this.selectedIndex();
      if (selected === emails.length - 1) {
        this.removeAt(selected);
      } else {
        this.selectedIndex.set(emails.length - 1);
        this.clearMessages();
      }
      return;
    }

    if (key === 'Escape') {
      this.selectedIndex.set(null);
      this.clearMessages();
      return;
    }

    if (key === 'Enter' || key === 'Tab' || key === ',' || key === ' ') {
      const value = draft.trim();
      if (!value) {
        if (key === 'Enter' || key === ',' || key === ' ') {
          event.preventDefault();
        }
        return;
      }

      // Tab: commit then allow focus to move when valid.
      if (key === 'Tab') {
        if (!this.tryCommit(value)) {
          event.preventDefault();
        }
        return;
      }

      event.preventDefault();
      this.tryCommit(value);
    }
  }

  onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text')?.trim();
    if (!text) return;

    const parts = text
      .split(/[\s,;]+/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length <= 1 && !/[\s,;]/.test(text)) {
      return;
    }

    event.preventDefault();
    this.selectedIndex.set(null);
    let lastInvalid: string | null = null;
    for (const part of parts) {
      if (!this.tryCommit(part)) {
        lastInvalid = part;
      }
    }
    if (lastInvalid) {
      this.error.set('Please enter a valid email address.');
      this.info.set(null);
      this.draft.set(lastInvalid);
      this.syncNativeInput();
    }
  }

  private tryCommit(raw: string): boolean {
    const email = raw.trim().toLowerCase();
    if (!email) {
      this.draft.set('');
      this.syncNativeInput();
      return true;
    }

    if (!EMAIL_PATTERN.test(email)) {
      this.error.set('Please enter a valid email address.');
      this.info.set(null);
      this.draft.set(raw.trim());
      this.syncNativeInput();
      return false;
    }

    const existing = this.emails().map((e) => e.toLowerCase());
    if (existing.includes(email) || this.normalizedExcluded().has(email)) {
      this.info.set('Duplicate email address');
      this.error.set(null);
      this.draft.set('');
      this.syncNativeInput();
      return true;
    }

    this.emails.set([...this.emails(), email]);
    this.draft.set('');
    this.selectedIndex.set(null);
    this.clearMessages();
    this.syncNativeInput();
    return true;
  }

  private syncNativeInput(): void {
    const el = this.draftInput?.nativeElement;
    if (el && el.value !== this.draft()) {
      el.value = this.draft();
    }
  }
}
